import { useCallback, useEffect, useRef, type RefObject } from 'react';
import { useDrawingStore } from '@/features/drawing/model';

interface UseTouchGesturesProps {
  canvasRef: RefObject<HTMLCanvasElement | null>;
  containerRef: RefObject<HTMLDivElement | null>;
  screenToCanvas: (
    clientX: number,
    clientY: number
  ) => { x: number; y: number };
  repositionCanvasIfNeeded: (point: { x: number; y: number }) => void;
  minZoomRef: RefObject<number>;
  transformRef: RefObject<{ zoom: number; pan: { x: number; y: number } }>;
  colorPickerOpen: boolean;
}

// Touch helper functions
const getTouchDistance = (touches: TouchList): number => {
  if (touches.length < 2) return 0;
  const touch1 = touches[0];
  const touch2 = touches[1];
  if (!touch1 || !touch2) return 0;
  const dx = touch1.clientX - touch2.clientX;
  const dy = touch1.clientY - touch2.clientY;
  return Math.sqrt(dx * dx + dy * dy);
};

const getTouchMidpoint = (touches: TouchList): { x: number; y: number } => {
  if (touches.length < 2) return { x: 0, y: 0 };
  const touch1 = touches[0];
  const touch2 = touches[1];
  if (!touch1 || !touch2) return { x: 0, y: 0 };
  return {
    x: (touch1.clientX + touch2.clientX) / 2,
    y: (touch1.clientY + touch2.clientY) / 2,
  };
};

export function useTouchGestures({
  canvasRef,
  containerRef,
  screenToCanvas,
  repositionCanvasIfNeeded,
  minZoomRef,
  transformRef,
  colorPickerOpen,
}: UseTouchGesturesProps) {
  const { tool, setCanvasTransform } = useDrawingStore();

  // Touch gesture state
  const touchStateRef = useRef<{
    isPinching: boolean;
    isPanning: boolean;
    initialDistance: number;
    initialScale: number;
    initialPosition: { x: number; y: number };
    initialMidpoint: { x: number; y: number };
  }>({
    isPinching: false,
    isPanning: false,
    initialDistance: 0,
    initialScale: 1,
    initialPosition: { x: 0, y: 0 },
    initialMidpoint: { x: 0, y: 0 },
  });

  // Text tool touch delay state
  const textToolTouchTimerRef = useRef<NodeJS.Timeout | null>(null);
  const pendingTextBlockRef = useRef<{ x: number; y: number } | null>(null);

  const handleTouchStartWrapper = useCallback(
    (e: TouchEvent) => {
      const currentTool = tool;

      if (colorPickerOpen) return;

      if (e.target === canvasRef.current) {
        e.preventDefault();
        e.stopPropagation();
      }

      const { handleTouchStart, startDrawing, createTextBlock } =
        useDrawingStore.getState();

      handleTouchStart(e.touches);

      // Two finger gestures: pinch zoom + pan
      if (e.touches.length === 2) {
        if (textToolTouchTimerRef.current) {
          clearTimeout(textToolTouchTimerRef.current);
          textToolTouchTimerRef.current = null;
        }
        pendingTextBlockRef.current = null;

        const distance = getTouchDistance(e.touches);
        const midpoint = getTouchMidpoint(e.touches);

        touchStateRef.current = {
          isPinching: true,
          isPanning: true,
          initialDistance: distance,
          initialScale: transformRef.current.zoom,
          initialPosition: transformRef.current.pan,
          initialMidpoint: midpoint,
        };
        return;
      }

      // Single finger: tool-specific actions
      if (e.touches.length === 1) {
        const touch = e.touches[0];
        if (!touch) return;

        const canvasPoint = screenToCanvas(touch.clientX, touch.clientY);

        if (currentTool === 'text') {
          const { textBlocks: currentTextBlocks } = useDrawingStore.getState();

          if (currentTextBlocks.activeBlockId !== null) {
            const { setActiveTextBlock } = useDrawingStore.getState();
            setActiveTextBlock(null);
            return;
          }

          pendingTextBlockRef.current = canvasPoint;

          if (textToolTouchTimerRef.current) {
            clearTimeout(textToolTouchTimerRef.current);
          }

          textToolTouchTimerRef.current = setTimeout(() => {
            if (pendingTextBlockRef.current) {
              createTextBlock(pendingTextBlockRef.current);
              repositionCanvasIfNeeded(pendingTextBlockRef.current);
              pendingTextBlockRef.current = null;
            }
          }, 200);
        } else {
          startDrawing(canvasPoint);
        }
      }
    },
    [
      screenToCanvas,
      colorPickerOpen,
      tool,
      repositionCanvasIfNeeded,
      canvasRef,
      transformRef,
    ]
  );

  const handleTouchMoveWrapper = useCallback(
    (e: TouchEvent) => {
      const currentTool = tool;

      if (colorPickerOpen) return;

      if (e.target === canvasRef.current) {
        e.preventDefault();
        e.stopPropagation();
      }

      const { handleTouchMove, continueDrawing } = useDrawingStore.getState();

      handleTouchMove(e.touches);

      // Two finger gestures - pinch zoom + pan
      if (e.touches.length === 2 && touchStateRef.current.isPinching) {
        const currentDistance = getTouchDistance(e.touches);
        const currentMidpoint = getTouchMidpoint(e.touches);
        const containerRect = containerRef.current?.getBoundingClientRect();

        if (
          !containerRect ||
          containerRect.width < 100 ||
          containerRect.height < 100
        ) {
          return;
        }

        const zoomOriginX =
          touchStateRef.current.initialMidpoint.x - containerRect.left;
        const zoomOriginY =
          touchStateRef.current.initialMidpoint.y - containerRect.top;

        const scaleDelta =
          currentDistance / touchStateRef.current.initialDistance;
        const newScale = Math.min(
          Math.max(
            touchStateRef.current.initialScale * scaleDelta,
            minZoomRef.current
          ),
          5
        );

        const imagePointX =
          (zoomOriginX -
            containerRect.width / 2 -
            touchStateRef.current.initialPosition.x) /
          touchStateRef.current.initialScale;
        const imagePointY =
          (zoomOriginY -
            containerRect.height / 2 -
            touchStateRef.current.initialPosition.y) /
          touchStateRef.current.initialScale;

        let newPanX =
          zoomOriginX - containerRect.width / 2 - imagePointX * newScale;
        let newPanY =
          zoomOriginY - containerRect.height / 2 - imagePointY * newScale;

        const midpointDeltaX =
          currentMidpoint.x - touchStateRef.current.initialMidpoint.x;
        const midpointDeltaY =
          currentMidpoint.y - touchStateRef.current.initialMidpoint.y;

        newPanX += midpointDeltaX;
        newPanY += midpointDeltaY;

        setCanvasTransform({
          zoom: newScale,
          pan: { x: newPanX, y: newPanY },
        });
        return;
      }

      if (currentTool === 'text') {
        return;
      }

      // Single finger: drawing
      const { drawing: drawingState } = useDrawingStore.getState();
      if (e.touches.length === 1 && drawingState.isDrawing) {
        const touch = e.touches[0];
        if (touch) {
          const canvasPoint = screenToCanvas(touch.clientX, touch.clientY);
          continueDrawing(canvasPoint);
        }
      }
    },
    [
      screenToCanvas,
      colorPickerOpen,
      tool,
      setCanvasTransform,
      containerRef,
      minZoomRef,
      canvasRef,
    ]
  );

  const handleTouchEndWrapper = useCallback(
    (e: TouchEvent) => {
      const currentTool = tool;

      if (colorPickerOpen) return;

      if (e.target === canvasRef.current) {
        e.preventDefault();
        e.stopPropagation();
      }

      // Reset touch state
      touchStateRef.current = {
        isPinching: false,
        isPanning: false,
        initialDistance: 0,
        initialScale: 1,
        initialPosition: { x: 0, y: 0 },
        initialMidpoint: { x: 0, y: 0 },
      };

      // For text tool: if user lifts finger before delay, immediately create text
      if (currentTool === 'text' && pendingTextBlockRef.current) {
        if (textToolTouchTimerRef.current) {
          clearTimeout(textToolTouchTimerRef.current);
          textToolTouchTimerRef.current = null;
        }

        const { textBlocks: currentTextBlocks } = useDrawingStore.getState();
        if (currentTextBlocks.activeBlockId === null) {
          const { createTextBlock } = useDrawingStore.getState();
          const blockPosition = pendingTextBlockRef.current;
          createTextBlock(blockPosition);
          repositionCanvasIfNeeded(blockPosition);
        }
        pendingTextBlockRef.current = null;
      }

      if (currentTool !== 'text') {
        const { handleTouchEnd, endDrawing } = useDrawingStore.getState();
        handleTouchEnd();
        endDrawing();
      }
    },
    [colorPickerOpen, tool, repositionCanvasIfNeeded, canvasRef]
  );

  // Add touch event listeners with passive: false
  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      canvas.addEventListener('touchstart', handleTouchStartWrapper, {
        passive: false,
      });
      canvas.addEventListener('touchmove', handleTouchMoveWrapper, {
        passive: false,
      });
      canvas.addEventListener('touchend', handleTouchEndWrapper, {
        passive: false,
      });
      return () => {
        canvas.removeEventListener('touchstart', handleTouchStartWrapper);
        canvas.removeEventListener('touchmove', handleTouchMoveWrapper);
        canvas.removeEventListener('touchend', handleTouchEndWrapper);

        if (textToolTouchTimerRef.current) {
          clearTimeout(textToolTouchTimerRef.current);
          textToolTouchTimerRef.current = null;
        }
        pendingTextBlockRef.current = null;
      };
    }
    return undefined;
  }, [
    handleTouchStartWrapper,
    handleTouchMoveWrapper,
    handleTouchEndWrapper,
    tool,
    canvasRef,
  ]);
}
